import { z } from "zod/v3";

// Tax Rate
export const TaxRate = z.object({
	value: z.number(),
	currency: z.string(),
});
export type TaxRate = z.infer<typeof TaxRate>;

// Tax Item
export const TaxItem = z.object({
	id: z.string(),
	label: z.string(),
	description: z.string().nullable().optional(),
	rate: TaxRate,
	country: z.string(),
	createdAt: z.string(),
	updatedAt: z.string(),
	/** HA-657 — the one standard rate: what a product with no rate of its own is taxed at. */
	isStandard: z.boolean().optional(),
});
export type TaxItem = z.infer<typeof TaxItem>;

// Business Tax Items (Aggregate)
export const BusinessTaxItems = z.object({
	id: z.string(),
	taxItems: z.array(TaxItem),
});
export type BusinessTaxItems = z.infer<typeof BusinessTaxItems>;

// Tax Item Form (for creation and updates)
export const TaxItemForm = z.object({
	id: z.string().optional(),
	label: z
		.string()
		.min(1, "Label is required")
		.max(200, "Label must be 200 characters or less"),
	description: z
		.string()
		.max(500, "Description must be 500 characters or less")
		.optional(),
	rate: z.object({
		// HA-232 AC3 (D-HA-29 29-3): a percentage from 0 to 100 inclusive with up to 2 decimal places.
		value: z
			.number({
				invalid_type_error: "Rate must be a number",
				required_error: "Rate is required",
			})
			.min(0, "Rate must be at least 0")
			.max(100, "Rate must be 100 or less")
			.refine(
				(v) => Math.abs(v * 100 - Math.round(v * 100)) < 1e-9,
				"Rate can have at most 2 decimal places",
			),
		currency: z.string().min(1, "Currency is required"),
	}),
	country: z
		.string()
		.min(2, "Country must be at least 2 characters")
		.max(100, "Country must be 100 characters or less"),
});
export type TaxItemForm = z.infer<typeof TaxItemForm>;
